import { Link, useLocation } from 'react-router-dom';
import { Book, Bookmark, Heart, Settings, Moon, Sun, Monitor, Download, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useTheme, usePWAInstall } from '../hooks/useQuran';
import useQuranStore from '../store/quranStore';

const TRANSLATIONS = [
  { value: 'english', label: 'English' },
  { value: 'urdu', label: 'Urdu' },
  { value: 'bengali', label: 'Bengali' },
  { value: 'turkish', label: 'Turkish' },
  { value: 'uzbek', label: 'Uzbek' },
];

const RECITERS = [
  { id: '1', name: 'Mishary Rashid Al Afasy' },
  { id: '2', name: 'Abu Bakr Al Shatri' },
  { id: '3', name: 'Nasser Al Qatami' },
  { id: '4', name: 'Yasser Al Dosari' },
  { id: '5', name: 'Hani Ar Rifai' },
];

export default function Header() {
  const location = useLocation();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { translationLang, setTranslationLang, reciterId, setReciterId } = useQuranStore();
  const { canInstall, promptInstall } = usePWAInstall();

  const navItems = [
    { path: '/', icon: Book, label: 'Surahs' },
    { path: '/bookmarks', icon: Bookmark, label: 'Bookmarks' },
    { path: '/favorites', icon: Heart, label: 'Favorites' },
  ];

  return (
    <header className="sticky top-0 z-40 w-full glass border-b border-border/40 safe-top" data-testid="header">
      <div className="container mx-auto px-4 safe-left safe-right">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-serif text-xl font-semibold hidden sm:block">
              Ahmad Quran
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={location.pathname === item.path ? 'secondary' : 'ghost'}
                  size="sm"
                  className="gap-2"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Translation Select */}
            <Select value={translationLang} onValueChange={setTranslationLang}>
              <SelectTrigger className="w-[120px] hidden sm:flex" data-testid="translation-select">
                <SelectValue placeholder="Translation" />
              </SelectTrigger>
              <SelectContent>
                {TRANSLATIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Reciter Select */}
            <Select value={reciterId} onValueChange={setReciterId}>
              <SelectTrigger className="w-[140px] hidden lg:flex" data-testid="reciter-select">
                <SelectValue placeholder="Reciter" />
              </SelectTrigger>
              <SelectContent>
                {RECITERS.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* PWA Install */}
            {canInstall && (
              <Button
                variant="outline"
                size="icon"
                onClick={promptInstall}
                className="hidden sm:flex"
                data-testid="install-pwa-btn"
              >
                <Download className="w-4 h-4" />
              </Button>
            )}

            {/* Theme Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="theme-toggle">
                  {resolvedTheme === 'dark' ? (
                    <Moon className="w-4 h-4" />
                  ) : (
                    <Sun className="w-4 h-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className="w-4 h-4 mr-2" />
                  Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className="w-4 h-4 mr-2" />
                  Dark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor className="w-4 h-4 mr-2" />
                  System
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Settings */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="md:hidden" data-testid="mobile-settings">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Settings</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="p-2">
                  <label className="text-sm font-medium mb-2 block">Theme</label>
                  <div className="flex gap-1">
                    <Button
                      variant={theme === 'light' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="flex-1 gap-1"
                      onClick={() => setTheme('light')}
                    >
                      <Sun className="w-3.5 h-3.5" />
                      Light
                    </Button>
                    <Button
                      variant={theme === 'dark' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="flex-1 gap-1"
                      onClick={() => setTheme('dark')}
                    >
                      <Moon className="w-3.5 h-3.5" />
                      Dark
                    </Button>
                    <Button
                      variant={theme === 'system' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="flex-1 gap-1"
                      onClick={() => setTheme('system')}
                    >
                      <Monitor className="w-3.5 h-3.5" />
                      Auto
                    </Button>
                  </div>
                </div>
                <div className="p-2">
                  <label className="text-sm font-medium mb-2 block">Translation</label>
                  <Select value={translationLang} onValueChange={setTranslationLang}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRANSLATIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="p-2">
                  <label className="text-sm font-medium mb-2 block">Reciter</label>
                  <Select value={reciterId} onValueChange={setReciterId}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECITERS.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DropdownMenuSeparator />
                {navItems.map((item) => (
                  <Link key={item.path} to={item.path}>
                    <DropdownMenuItem>
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  </Link>
                ))}
                {canInstall && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={promptInstall}>
                      <Download className="w-4 h-4 mr-2" />
                      Install App
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
